import type { Locale } from "@/lib/i18n";

type Faq = { question: string; answer: string };

type MatrixSection = {
  group: string;
  rows: ReadonlyArray<readonly [string, string, string, string]>;
};

type PlanCard = { tag: string; title: string; body: string };

type SourceLink = { href: string; label: string; note: string };

type ReadingLink = { href: string; label: string };

export type Content = {
  breadcrumbHome: string;
  breadcrumbCurrent: string;
  eyebrow: string;
  h1: string;
  heroLede: string;
  /** In-text link to the "Claude partner Malaysia" owner page (2026-10-08). */
  heroPartnerLead: string;
  heroPartnerLink: string;
  heroPartnerTail: string;
  shortAnswerLabel: string;
  shortAnswerQuestion: string;
  shortAnswerBodyBeforePersonal: string;
  shortAnswerPersonal: string;
  shortAnswerBetweenPersonalAndCompany: string;
  shortAnswerCompany: string;
  shortAnswerAfterCompany: string;
  shortAnswerCanEmphasis: string;
  shortAnswerAfterCan: string;
  matrixEyebrow: string;
  matrixColIndividual: string;
  matrixColTeam: string;
  matrixColEnterprise: string;
  matrix: ReadonlyArray<MatrixSection>;
  matrixFootnote: string;
  planEyebrow: string;
  planHeading: string;
  planPrivacy: ReadonlyArray<PlanCard>;
  trainingEyebrow: string;
  trainingHeading: string;
  trainingParaOneBefore: string;
  trainingParaOneEmphasis: string;
  trainingParaOneAfter: string;
  trainingParaTwo: string;
  pdpaEyebrow: string;
  pdpaHeading: string;
  pdpaParaOne: string;
  pdpaParaTwoBefore: string;
  pdpaParaTwoEmphasis: string;
  pdpaParaTwoAfter: string;
  faqEyebrow: string;
  faqs: ReadonlyArray<Faq>;
  sourcesEyebrow: string;
  sourcesHeading: string;
  sourcesIntro: string;
  sources: ReadonlyArray<SourceLink>;
  disclaimerLabel: string;
  disclaimerBeforeLink: string;
  disclaimerLinkText: string;
  disclaimerAfterLink: string;
  keepReadingLabel: string;
  readingLinks: ReadonlyArray<ReadingLink>;
  ctaTitle: string;
  ctaDescription: string;
  ctaButtonText: string;
};

const en: Content = {
  breadcrumbHome: "Home",
  breadcrumbCurrent: "Claude Privacy & Compliance",
  eyebrow: "Privacy & compliance guide",
  h1: "Claude privacy & compliance: Individual, Team & Enterprise",
  heroLede:
    "Can your admin see your chats? Does Anthropic train on your data? What compliance does Claude actually hold? A plain-English guide for Malaysian teams weighing a rollout — compiled from Anthropic's Privacy Center, Trust Center, and Help Center.",
  heroPartnerLead: " Written by a ",
  heroPartnerLink: "Claude partner in Malaysia",
  heroPartnerTail: " that sets up Claude for business teams.",
  shortAnswerLabel: "The short answer",
  shortAnswerQuestion: "“Can my admin see my Claude chat?”",
  shortAnswerBodyBeforePersonal: "On a ",
  shortAnswerPersonal: "personal plan (Free, Pro, Max)",
  shortAnswerBetweenPersonalAndCompany:
    ", no — there is no admin, and only you see your chats. On a ",
  shortAnswerCompany: "company plan (Team or Enterprise)",
  shortAnswerAfterCompany:
    ", treat Claude like work email: it is not casually monitored, but your organisation ",
  shortAnswerCanEmphasis: "can",
  shortAnswerAfterCan:
    " access your conversations when it has a reason — a Primary-Owner data export on Team, and the Compliance API on Enterprise.",
  matrixEyebrow: "01 · Privacy & compliance at a glance",
  matrixColIndividual: "Individual",
  matrixColTeam: "Team",
  matrixColEnterprise: "Enterprise",
  matrix: [
    {
      group: "Who can see your chats",
      rows: [
        [
          "Who can read your conversations",
          "Only you — there is no admin",
          "You; the Primary Owner can export your data",
          "You; the org can also access content via the Compliance API",
        ],
        ["Visible to teammates?", "No", "Only Projects you choose to share", "Only Projects you choose to share"],
      ],
    },
    {
      group: "Data use & retention",
      rows: [
        ["Used to train Claude?", "Your choice (opt-in since 8 Oct 2025)", "Never, by default", "Never, by default"],
        [
          "Data retention",
          "30 days (declined) to 5 years (allowed)",
          "Standard; deleted chats are removed",
          "Custom period, min 30 days; audit-logged",
        ],
      ],
    },
    {
      group: "Governance & compliance",
      rows: [
        ["Central admin controls", "—", "Seats, SSO, spend caps, usage analytics", "+ SCIM, role-based access, audit logs"],
        ["Compliance API (content access)", "—", "—", "Yes"],
        ["Compliance certifications", "Consumer plan — not covered", "SOC 2, ISO 27001 & 42001", "SOC 2, ISO 27001 & 42001"],
        ["HIPAA BAA available", "—", "—", "Yes — sales-assisted"],
      ],
    },
  ],
  matrixFootnote:
    "Compiled from Anthropic's Privacy Center, Trust Center, and Help Center, accurate at July 2026. Certifications cover commercial products (Claude for Work and the API), not consumer plans.",
  planEyebrow: "02 · Who can see your chats, plan by plan",
  planHeading: "The honest answer for each plan",
  planPrivacy: [
    {
      tag: "Individual · Free / Pro / Max",
      title: "Private to you — but you choose on training",
      body: "No admin, no organisation. Only you can see your chats, and Anthropic staff access is limited (safety classifiers may review flagged content). Since 8 October 2025 you decide whether your chats improve Claude: allow it and retention runs to 5 years, decline it and it stays at 30 days.",
    },
    {
      tag: "Team · 2–150 seats",
      title: "Not casually monitored — but the owner can export",
      body: "Regular admins get seat, spend, and usage controls, not a window into your chats. The Primary Owner, however, can request a data export that may include your conversations and files. Your data is never used for training. Personal chats stay private unless you share a Project.",
    },
    {
      tag: "Enterprise · regulated & large orgs",
      title: "Full governance — including content access",
      body: "Everything in Team plus SSO/SCIM, role-based access, audit logs, custom retention, and a Compliance API that gives the organisation programmatic access to usage data and conversation content for eDiscovery and monitoring. This is the plan for organisations that must be able to access and govern content — by design.",
    },
  ],
  trainingEyebrow: "03 · Training & data retention",
  trainingHeading: "Does Anthropic train on your data, and for how long is it kept?",
  trainingParaOneBefore: "The single most important distinction is ",
  trainingParaOneEmphasis: "consumer versus commercial",
  trainingParaOneAfter:
    ". On Team, Enterprise, and the API, Anthropic does not use your prompts and responses to train its models by default — this is the reason most businesses should put sensitive work on a commercial plan rather than a personal one.",
  trainingParaTwo:
    "On individual Free, Pro, and Max plans the position changed on 8 October 2025: you now choose whether your chats improve Claude. Allow it and retention extends to five years; decline it and the standard 30-day window applies. Deleting a chat removes it from your history, after which it is cleared from Anthropic's back-end systems per their policy. On Enterprise, a Primary Owner or Owner sets a custom retention period — a minimum of 30 days, with data kept indefinitely unless a period is configured — and every retention change and deletion is written to an audit log.",
  pdpaEyebrow: "04 · What this means for PDPA in Malaysia",
  pdpaHeading: "Using Claude with PDPA-regulated data",
  pdpaParaOne:
    "For a Malaysian business subject to the Personal Data Protection Act, the model is only half the picture. A commercial Claude plan gives you the raw ingredients — no training on your data, configurable retention, SOC 2 and ISO 27001/42001 assurance, and on Enterprise the audit logs, role-based access, and a data-processing agreement or BAA that regulated work needs.",
  pdpaParaTwoBefore: "But PDPA compliance turns on ",
  pdpaParaTwoEmphasis: "how you deploy and govern it",
  pdpaParaTwoAfter:
    ": minimise the personal data you send, control who can access it, sign the right processor agreement, and be deliberate about where inference runs. That is the work we scope with clients — and it is a conversation for your data protection officer or counsel, not something the model settles on its own. This page is a starting point, not legal advice.",
  faqEyebrow: "05 · Frequently asked questions",
  faqs: [
    {
      question: "Can my company or admin read my Claude chats on a Team or Enterprise plan?",
      answer:
        "Not through a casual admin dashboard. On the Team plan, regular admins have no feature to browse your conversations — but the organisation's Primary Owner can request access to your user data through a data export, which may contain your conversations, uploaded files, and usage patterns. On Enterprise, the organisation can additionally access conversation content programmatically through the Compliance API and set data-retention rules. So on a company plan, treat Claude like work email: it is not casually monitored, but the company can access it when it has a legitimate reason. On a personal Free, Pro, or Max plan there is no admin — only you can see your chats.",
    },
    {
      question: "Does Anthropic train Claude on my conversations?",
      answer:
        "On Team, Enterprise, and the API (including Amazon Bedrock and Google Vertex AI), no — customer prompts and responses are not used to train Anthropic's models by default. On individual Free, Pro, and Max plans the rule changed on 8 October 2025: you now choose. Allow your chats to improve Claude and retention extends to 5 years; decline and the standard 30-day retention applies.",
    },
    {
      question: "How long are my Claude conversations kept?",
      answer:
        "On individual plans, 30 days if you decline model training, or up to 5 years if you allow it; deleting a chat removes it from your history and it is then removed from Anthropic's back-end systems per their retention policy. On Team, standard retention applies and deleted chats are removed. On Enterprise, a Primary Owner or Owner sets a custom retention period (minimum 30 days; data is retained indefinitely unless a period is set), and all retention changes and deletions are recorded in audit logs.",
    },
    {
      question: "Is Claude SOC 2 or HIPAA compliant?",
      answer:
        "For its commercial products — Claude for Work (Team and Enterprise) and the Anthropic API — Anthropic maintains SOC 2 Type I and Type II, ISO/IEC 27001:2022, and ISO/IEC 42001:2023, plus a HIPAA-ready configuration with a Business Associate Agreement (BAA) available on sales-assisted Enterprise and the first-party API. Consumer Free/Pro/Max plans are not the vehicle for these commitments. Current certifications and the sub-processor list are published in Anthropic's Trust Center.",
    },
    {
      question: "Can I use Claude with PDPA-regulated personal data in Malaysia?",
      answer:
        "Yes, with the right plan and setup. Commercial plans do not train on your data and support retention controls; Enterprise adds audit logs, role-based access, a Compliance API, and a BAA/data-processing-agreement path. But PDPA compliance is mostly about how you deploy and govern Claude — data minimisation, access control, a processor agreement, and where inference runs — not the model alone. Treat this page as a starting point for your DPO or counsel, not legal advice.",
    },
    {
      question: "Are my chats private from other people on my team?",
      answer:
        "Yes. Your individual chats are private to you unless you explicitly share a Project or conversation. Sharing a Project gives the people you choose 'Can view' or 'Can edit' access to that project's chats, knowledge, and instructions — but your other personal chats stay private and are not exposed to teammates.",
    },
    {
      question: "What is the difference between Team and Enterprise for privacy and compliance?",
      answer:
        "Both keep your data out of model training and give you central admin and SSO. Enterprise adds SCIM provisioning, fine-grained role-based access control, audit logs, the Compliance API for programmatic access to usage data and content, custom data-retention timelines, and options such as customer-managed encryption keys, US-only inference, and a HIPAA BAA — the governance controls regulated organisations typically need.",
    },
  ],
  sourcesEyebrow: "06 · Sources",
  sourcesHeading: "Verified against Anthropic's own documentation",
  sourcesIntro:
    "Every claim on this page is drawn from Anthropic's primary sources, listed here so you can verify each point directly rather than take our word for it:",
  sources: [
    {
      href: "https://privacy.claude.com/en/articles/9265372-who-owns-and-manages-the-data-of-my-team",
      label: "Anthropic Privacy Center — Who owns and manages the data of my team?",
      note: " — the Primary Owner can request access to member data via data exports that may include conversations, files, and usage.",
    },
    {
      href: "https://www.anthropic.com/news/claude-code-on-team-and-enterprise",
      label: "Anthropic — Claude Code and new admin controls for business plans",
      note: " — the Enterprise Compliance API gives real-time programmatic access to usage data and customer content.",
    },
    {
      href: "https://www.anthropic.com/news/updates-to-our-consumer-terms",
      label: "Anthropic — Updates to Consumer Terms and Privacy Policy",
      note: " — the 8 October 2025 model-training choice for Free/Pro/Max and the 5-year vs 30-day retention split; does not apply to commercial plans.",
    },
    {
      href: "https://privacy.claude.com/en/articles/10440198-custom-data-retention-controls-for-claude-enterprise",
      label: "Anthropic Privacy Center — Custom data retention controls for Claude Enterprise",
      note: " — minimum 30-day retention, indefinite by default, and audit logging of all retention changes.",
    },
    {
      href: "https://privacy.claude.com/en/articles/10015870-do-you-have-a-soc-2-or-hipaa-certifications",
      label: "Anthropic Privacy Center — Do you have SOC 2 or HIPAA certifications?",
      note: " — SOC 2 Type I & II, ISO/IEC 27001:2022, ISO/IEC 42001:2023, and HIPAA-ready with a BAA for commercial products.",
    },
    {
      href: "https://trust.anthropic.com",
      label: "Anthropic Trust Center",
      note: " — the authoritative, always-current list of certifications and sub-processors.",
    },
  ],
  disclaimerLabel: "Disclaimer",
  disclaimerBeforeLink:
    "All privacy and compliance information on this page is compiled from publicly available information on Anthropic's Privacy Center, Trust Center, and Help Center and is provided for reference and research only, accurate at July 2026 and subject to change at Anthropic's discretion. It is not legal advice; confirm your obligations with your own data protection officer or counsel. Anchor Sprint is a deployment and rollout partner, not a reseller. Claude and Anthropic are trademarks of Anthropic, PBC. Anchor Sprint is Certified in the Claude Partner Network; this does not imply endorsement of our services. For authoritative security and compliance documentation, see Anthropic's ",
  disclaimerLinkText: "Trust Center",
  disclaimerAfterLink: ".",
  keepReadingLabel: "Keep reading",
  readingLinks: [
    { href: "/guides/claude-cowork/", label: "What is Claude Cowork?" },
    { href: "/guides/claude-plans-malaysia/", label: "Compare every Claude plan" },
    { href: "/blog/claude-ai-pricing-malaysia-2026/", label: "Claude AI pricing in Malaysia" },
    { href: "/services/ai-solutions/", label: "Explore our AI Solutions" },
  ],
  ctaTitle: "Rolling out Claude on sensitive or PDPA-regulated data?",
  ctaDescription:
    "Tell us your team size, the data Claude will touch, and your compliance obligations. We will recommend the right plan and configure a PDPA-aligned deployment — access controls, retention, and processor agreements — then help you roll it out. We do not resell seats.",
  ctaButtonText: "Talk to Our Team",
};

const ms: Content = {
  breadcrumbHome: "Laman Utama",
  breadcrumbCurrent: "Privasi & Pematuhan Claude",
  eyebrow: "Panduan privasi & pematuhan",
  h1: "Privasi & pematuhan Claude: Individual, Team & Enterprise",
  heroLede:
    "Bolehkah admin anda melihat perbualan anda? Adakah Anthropic melatih model pada data anda? Pematuhan apa yang sebenarnya dipegang oleh Claude? Panduan ringkas dan jelas untuk pasukan Malaysia yang sedang menimbang pelaksanaan — disusun daripada Privacy Center, Trust Center dan Help Center Anthropic.",
  heroPartnerLead: " Ditulis oleh ",
  heroPartnerLink: "rakan kongsi Claude di Malaysia",
  heroPartnerTail: " yang menyediakan Claude untuk pasukan perniagaan.",
  shortAnswerLabel: "Jawapan ringkas",
  shortAnswerQuestion: "“Bolehkah admin saya melihat perbualan Claude saya?”",
  shortAnswerBodyBeforePersonal: "Pada ",
  shortAnswerPersonal: "pelan peribadi (Free, Pro, Max)",
  shortAnswerBetweenPersonalAndCompany:
    ", tidak — tiada admin, dan hanya anda yang melihat perbualan anda. Pada ",
  shortAnswerCompany: "pelan syarikat (Team atau Enterprise)",
  shortAnswerAfterCompany:
    ", anggaplah Claude seperti e-mel kerja: ia tidak dipantau secara sambil lewa, tetapi organisasi anda ",
  shortAnswerCanEmphasis: "boleh",
  shortAnswerAfterCan:
    " mengakses perbualan anda apabila terdapat sebab yang sah — eksport data oleh Primary Owner pada Team, dan Compliance API pada Enterprise.",
  matrixEyebrow: "01 · Privasi & pematuhan sepintas lalu",
  matrixColIndividual: "Individual",
  matrixColTeam: "Team",
  matrixColEnterprise: "Enterprise",
  matrix: [
    {
      group: "Siapa yang boleh melihat perbualan anda",
      rows: [
        [
          "Siapa yang boleh membaca perbualan anda",
          "Hanya anda — tiada admin",
          "Anda; Primary Owner boleh mengeksport data anda",
          "Anda; organisasi juga boleh mengakses kandungan melalui Compliance API",
        ],
        [
          "Kelihatan kepada rakan sepasukan?",
          "Tidak",
          "Hanya Projects yang anda pilih untuk dikongsi",
          "Hanya Projects yang anda pilih untuk dikongsi",
        ],
      ],
    },
    {
      group: "Penggunaan & pengekalan data",
      rows: [
        [
          "Digunakan untuk melatih Claude?",
          "Pilihan anda (opt-in sejak 8 Oct 2025)",
          "Tidak sekali-kali, secara lalai",
          "Tidak sekali-kali, secara lalai",
        ],
        [
          "Pengekalan data",
          "30 days (menolak) hingga 5 years (membenarkan)",
          "Standard; perbualan yang dipadam dibuang",
          "Tempoh tersuai, min 30 days; direkod dalam audit log",
        ],
      ],
    },
    {
      group: "Tadbir urus & pematuhan",
      rows: [
        [
          "Kawalan admin berpusat",
          "—",
          "Seats, SSO, had perbelanjaan, analitik penggunaan",
          "+ SCIM, akses berasaskan peranan, audit logs",
        ],
        ["Compliance API (akses kandungan)", "—", "—", "Ya"],
        ["Persijilan pematuhan", "Pelan pengguna — tidak dilindungi", "SOC 2, ISO 27001 & 42001", "SOC 2, ISO 27001 & 42001"],
        ["HIPAA BAA tersedia", "—", "—", "Ya — dibantu jualan"],
      ],
    },
  ],
  matrixFootnote:
    "Disusun daripada Privacy Center, Trust Center dan Help Center Anthropic, tepat pada July 2026. Persijilan meliputi produk komersial (Claude for Work dan API), bukan pelan pengguna.",
  planEyebrow: "02 · Siapa yang boleh melihat perbualan anda, pelan demi pelan",
  planHeading: "Jawapan jujur bagi setiap pelan",
  planPrivacy: [
    {
      tag: "Individual · Free / Pro / Max",
      title: "Peribadi kepada anda — tetapi anda memilih tentang latihan",
      body: "Tiada admin, tiada organisasi. Hanya anda yang boleh melihat perbualan anda, dan akses kakitangan Anthropic adalah terhad (pengelas keselamatan mungkin menyemak kandungan yang ditandakan). Sejak 8 October 2025 anda memutuskan sama ada perbualan anda menambah baik Claude: benarkan dan pengekalan berjalan sehingga 5 years, tolak dan ia kekal pada 30 days.",
    },
    {
      tag: "Team · 2–150 seats",
      title: "Tidak dipantau secara sambil lewa — tetapi owner boleh mengeksport",
      body: "Admin biasa mendapat kawalan seat, perbelanjaan dan penggunaan, bukan jendela ke dalam perbualan anda. Namun, Primary Owner boleh meminta eksport data yang mungkin merangkumi perbualan dan fail anda. Data anda tidak sekali-kali digunakan untuk latihan. Perbualan peribadi kekal peribadi melainkan anda berkongsi Project.",
    },
    {
      tag: "Enterprise · organisasi terkawal & besar",
      title: "Tadbir urus penuh — termasuk akses kandungan",
      body: "Semua dalam Team ditambah SSO/SCIM, akses berasaskan peranan, audit logs, pengekalan tersuai, dan Compliance API yang memberi organisasi akses program kepada data penggunaan dan kandungan perbualan untuk eDiscovery dan pemantauan. Ini pelan untuk organisasi yang mesti mampu mengakses dan mentadbir kandungan — memang direka sedemikian.",
    },
  ],
  trainingEyebrow: "03 · Latihan & pengekalan data",
  trainingHeading: "Adakah Anthropic melatih model pada data anda, dan berapa lama ia disimpan?",
  trainingParaOneBefore: "Perbezaan tunggal yang paling penting ialah ",
  trainingParaOneEmphasis: "pengguna berbanding komersial",
  trainingParaOneAfter:
    ". Pada Team, Enterprise dan API, Anthropic tidak menggunakan prompt dan respons anda untuk melatih modelnya secara lalai — inilah sebabnya kebanyakan perniagaan patut meletakkan kerja sensitif pada pelan komersial dan bukan pelan peribadi.",
  trainingParaTwo:
    "Pada pelan individu Free, Pro dan Max, kedudukannya berubah pada 8 October 2025: kini anda memilih sama ada perbualan anda menambah baik Claude. Benarkan dan pengekalan dilanjutkan sehingga lima tahun; tolak dan tetingkap standard 30-day terpakai. Memadam perbualan membuangnya daripada sejarah anda, selepas itu ia dibersihkan daripada sistem back-end Anthropic mengikut dasar mereka. Pada Enterprise, Primary Owner atau Owner menetapkan tempoh pengekalan tersuai — minimum 30 days, dengan data disimpan tanpa had melainkan tempoh dikonfigurasikan — dan setiap perubahan pengekalan serta pemadaman ditulis ke audit log.",
  pdpaEyebrow: "04 · Apa maksudnya untuk PDPA di Malaysia",
  pdpaHeading: "Menggunakan Claude dengan data yang dikawal PDPA",
  pdpaParaOne:
    "Bagi perniagaan Malaysia yang tertakluk kepada Akta Perlindungan Data Peribadi, model hanyalah separuh gambaran. Pelan Claude komersial memberi anda bahan asasnya — tiada latihan pada data anda, pengekalan boleh dikonfigurasi, jaminan SOC 2 dan ISO 27001/42001, dan pada Enterprise, audit logs, akses berasaskan peranan, serta perjanjian pemprosesan data atau BAA yang diperlukan oleh kerja terkawal.",
  pdpaParaTwoBefore: "Tetapi pematuhan PDPA bergantung pada ",
  pdpaParaTwoEmphasis: "cara anda melaksanakan dan mentadbirnya",
  pdpaParaTwoAfter:
    ": kurangkan data peribadi yang anda hantar, kawal siapa yang boleh mengaksesnya, tandatangani perjanjian pemproses yang betul, dan bijak tentang di mana inferens dijalankan. Itulah kerja yang kami skop bersama pelanggan — dan ia perbincangan untuk pegawai perlindungan data atau penasihat undang-undang anda, bukan sesuatu yang model selesaikan sendiri. Halaman ini titik permulaan, bukan nasihat undang-undang.",
  faqEyebrow: "05 · Soalan lazim",
  faqs: [
    {
      question: "Bolehkah syarikat atau admin saya membaca perbualan Claude saya pada pelan Team atau Enterprise?",
      answer:
        "Bukan melalui papan pemuka admin biasa. Pada pelan Team, admin biasa tiada ciri untuk menyemak imbas perbualan anda — tetapi Primary Owner organisasi boleh meminta akses kepada data pengguna anda melalui eksport data, yang mungkin mengandungi perbualan, fail yang dimuat naik, dan corak penggunaan anda. Pada Enterprise, organisasi tambahan boleh mengakses kandungan perbualan secara program melalui Compliance API dan menetapkan peraturan pengekalan data. Jadi pada pelan syarikat, anggaplah Claude seperti e-mel kerja: ia tidak dipantau secara sambil lewa, tetapi syarikat boleh mengaksesnya apabila terdapat sebab yang sah. Pada pelan peribadi Free, Pro atau Max tiada admin — hanya anda yang boleh melihat perbualan anda.",
    },
    {
      question: "Adakah Anthropic melatih Claude pada perbualan saya?",
      answer:
        "Pada Team, Enterprise dan API (termasuk Amazon Bedrock dan Google Vertex AI), tidak — prompt dan respons pelanggan tidak digunakan untuk melatih model Anthropic secara lalai. Pada pelan individu Free, Pro dan Max, peraturannya berubah pada 8 October 2025: kini anda memilih. Benarkan perbualan anda menambah baik Claude dan pengekalan dilanjutkan sehingga 5 years; tolak dan pengekalan standard 30-day terpakai.",
    },
    {
      question: "Berapa lama perbualan Claude saya disimpan?",
      answer:
        "Pada pelan individu, 30 days jika anda menolak latihan model, atau sehingga 5 years jika anda membenarkannya; memadam perbualan membuangnya daripada sejarah anda dan ia kemudian dibuang daripada sistem back-end Anthropic mengikut dasar pengekalan mereka. Pada Team, pengekalan standard terpakai dan perbualan yang dipadam dibuang. Pada Enterprise, Primary Owner atau Owner menetapkan tempoh pengekalan tersuai (minimum 30 days; data disimpan tanpa had melainkan tempoh ditetapkan), dan semua perubahan pengekalan serta pemadaman direkod dalam audit logs.",
    },
    {
      question: "Adakah Claude mematuhi SOC 2 atau HIPAA?",
      answer:
        "Bagi produk komersialnya — Claude for Work (Team dan Enterprise) dan Anthropic API — Anthropic mengekalkan SOC 2 Type I dan Type II, ISO/IEC 27001:2022, dan ISO/IEC 42001:2023, serta konfigurasi sedia HIPAA dengan Business Associate Agreement (BAA) yang tersedia pada Enterprise yang dibantu jualan dan API pihak pertama. Pelan pengguna Free/Pro/Max bukan wadah untuk komitmen ini. Persijilan semasa dan senarai sub-pemproses diterbitkan dalam Trust Center Anthropic.",
    },
    {
      question: "Bolehkah saya menggunakan Claude dengan data peribadi yang dikawal PDPA di Malaysia?",
      answer:
        "Ya, dengan pelan dan persediaan yang betul. Pelan komersial tidak melatih pada data anda dan menyokong kawalan pengekalan; Enterprise menambah audit logs, akses berasaskan peranan, Compliance API, dan laluan BAA/perjanjian pemprosesan data. Tetapi pematuhan PDPA kebanyakannya tentang cara anda melaksanakan dan mentadbir Claude — pengurangan data, kawalan akses, perjanjian pemproses, dan di mana inferens dijalankan — bukan model semata-mata. Anggaplah halaman ini sebagai titik permulaan untuk DPO atau penasihat undang-undang anda, bukan nasihat undang-undang.",
    },
    {
      question: "Adakah perbualan saya peribadi daripada orang lain dalam pasukan saya?",
      answer:
        "Ya. Perbualan individu anda peribadi kepada anda melainkan anda secara jelas berkongsi Project atau perbualan. Berkongsi Project memberi orang yang anda pilih akses 'Can view' atau 'Can edit' kepada perbualan, pengetahuan, dan arahan projek itu — tetapi perbualan peribadi anda yang lain kekal peribadi dan tidak didedahkan kepada rakan sepasukan.",
    },
    {
      question: "Apakah perbezaan antara Team dan Enterprise dari segi privasi dan pematuhan?",
      answer:
        "Kedua-duanya mengekalkan data anda daripada latihan model dan memberi anda admin berpusat serta SSO. Enterprise menambah peruntukan SCIM, kawalan akses berasaskan peranan yang terperinci, audit logs, Compliance API untuk akses program kepada data penggunaan dan kandungan, garis masa pengekalan data tersuai, serta pilihan seperti kunci penyulitan yang diurus pelanggan, inferens khusus US, dan HIPAA BAA — kawalan tadbir urus yang biasanya diperlukan oleh organisasi terkawal.",
    },
  ],
  sourcesEyebrow: "06 · Sumber",
  sourcesHeading: "Disahkan terhadap dokumentasi Anthropic sendiri",
  sourcesIntro:
    "Setiap dakwaan pada halaman ini diambil daripada sumber utama Anthropic, disenaraikan di sini supaya anda boleh mengesahkan setiap perkara secara langsung dan bukan sekadar mempercayai kata kami:",
  sources: [
    {
      href: "https://privacy.claude.com/en/articles/9265372-who-owns-and-manages-the-data-of-my-team",
      label: "Anthropic Privacy Center — Who owns and manages the data of my team?",
      note: " — Primary Owner boleh meminta akses kepada data ahli melalui eksport data yang mungkin merangkumi perbualan, fail, dan penggunaan.",
    },
    {
      href: "https://www.anthropic.com/news/claude-code-on-team-and-enterprise",
      label: "Anthropic — Claude Code and new admin controls for business plans",
      note: " — Compliance API Enterprise memberi akses program masa nyata kepada data penggunaan dan kandungan pelanggan.",
    },
    {
      href: "https://www.anthropic.com/news/updates-to-our-consumer-terms",
      label: "Anthropic — Updates to Consumer Terms and Privacy Policy",
      note: " — pilihan latihan model 8 October 2025 untuk Free/Pro/Max dan pembahagian pengekalan 5-year berbanding 30-day; tidak terpakai pada pelan komersial.",
    },
    {
      href: "https://privacy.claude.com/en/articles/10440198-custom-data-retention-controls-for-claude-enterprise",
      label: "Anthropic Privacy Center — Custom data retention controls for Claude Enterprise",
      note: " — pengekalan minimum 30-day, tanpa had secara lalai, dan pengelogan audit bagi semua perubahan pengekalan.",
    },
    {
      href: "https://privacy.claude.com/en/articles/10015870-do-you-have-a-soc-2-or-hipaa-certifications",
      label: "Anthropic Privacy Center — Do you have SOC 2 or HIPAA certifications?",
      note: " — SOC 2 Type I & II, ISO/IEC 27001:2022, ISO/IEC 42001:2023, dan sedia HIPAA dengan BAA untuk produk komersial.",
    },
    {
      href: "https://trust.anthropic.com",
      label: "Anthropic Trust Center",
      note: " — senarai berautoriti dan sentiasa terkini bagi persijilan dan sub-pemproses.",
    },
  ],
  disclaimerLabel: "Penafian",
  disclaimerBeforeLink:
    "Semua maklumat privasi dan pematuhan pada halaman ini disusun daripada maklumat yang tersedia secara umum di Privacy Center, Trust Center dan Help Center Anthropic dan disediakan untuk rujukan dan penyelidikan sahaja, tepat pada July 2026 dan tertakluk kepada perubahan atas budi bicara Anthropic. Ia bukan nasihat undang-undang; sahkan kewajipan anda dengan pegawai perlindungan data atau penasihat undang-undang anda sendiri. Anchor Sprint ialah rakan pelaksanaan dan pengedaran, bukan penjual semula. Claude and Anthropic are trademarks of Anthropic, PBC. Anchor Sprint is Certified in the Claude Partner Network; ini tidak membayangkan sokongan terhadap perkhidmatan kami. Untuk dokumentasi keselamatan dan pematuhan yang berautoriti, lihat ",
  disclaimerLinkText: "Trust Center",
  disclaimerAfterLink: " Anthropic.",
  keepReadingLabel: "Teruskan membaca",
  readingLinks: [
    { href: "/guides/claude-cowork/", label: "Apakah itu Claude Cowork?" },
    { href: "/guides/claude-plans-malaysia/", label: "Bandingkan setiap pelan Claude" },
    { href: "/blog/claude-ai-pricing-malaysia-2026/", label: "Harga Claude AI di Malaysia" },
    { href: "/services/ai-solutions/", label: "Terokai AI Solutions kami" },
  ],
  ctaTitle: "Melaksanakan Claude pada data sensitif atau yang dikawal PDPA?",
  ctaDescription:
    "Beritahu kami saiz pasukan anda, data yang akan disentuh Claude, dan kewajipan pematuhan anda. Kami akan mencadangkan pelan yang betul dan mengkonfigurasi pelaksanaan yang selaras dengan PDPA — kawalan akses, pengekalan, dan perjanjian pemproses — kemudian membantu anda melaksanakannya. Kami tidak menjual semula seats.",
  ctaButtonText: "Hubungi Pasukan Kami",
};

const zh: Content = {
  breadcrumbHome: "首页",
  breadcrumbCurrent: "Claude 隐私与合规",
  eyebrow: "隐私与合规指南",
  h1: "Claude 隐私与合规：Individual、Team 与 Enterprise",
  heroLede:
    "您的管理员能看到您的对话吗？Anthropic 会用您的数据训练模型吗？Claude 实际拥有哪些合规资质？这是一份为正在权衡是否部署的马来西亚团队而写的通俗易懂指南——整理自 Anthropic 的 Privacy Center、Trust Center 与 Help Center。",
  heroPartnerLead: "本指南由",
  heroPartnerLink: "马来西亚的 Claude 合作伙伴",
  heroPartnerTail: "撰写，我们为企业团队部署 Claude。",
  shortAnswerLabel: "简短答案",
  shortAnswerQuestion: "“我的管理员能看到我的 Claude 对话吗？”",
  shortAnswerBodyBeforePersonal: "在",
  shortAnswerPersonal: "个人方案（Free、Pro、Max）",
  shortAnswerBetweenPersonalAndCompany: "上，不能——没有管理员，只有您能看到自己的对话。在",
  shortAnswerCompany: "企业方案（Team 或 Enterprise）",
  shortAnswerAfterCompany: "上，请把 Claude 当作工作电子邮件：它不会被随意监控，但当有正当理由时，您的组织",
  shortAnswerCanEmphasis: "可以",
  shortAnswerAfterCan:
    "访问您的对话——在 Team 上是通过 Primary Owner 的数据导出，在 Enterprise 上是通过 Compliance API。",
  matrixEyebrow: "01 · 隐私与合规一览",
  matrixColIndividual: "Individual",
  matrixColTeam: "Team",
  matrixColEnterprise: "Enterprise",
  matrix: [
    {
      group: "谁能看到您的对话",
      rows: [
        [
          "谁能读取您的对话",
          "只有您——没有管理员",
          "您；Primary Owner 可以导出您的数据",
          "您；组织还可通过 Compliance API 访问内容",
        ],
        ["队友可见吗？", "不可见", "仅限您选择共享的 Projects", "仅限您选择共享的 Projects"],
      ],
    },
    {
      group: "数据使用与保留",
      rows: [
        ["用于训练 Claude 吗？", "由您选择（自 8 Oct 2025 起可选择加入）", "默认绝不", "默认绝不"],
        [
          "数据保留",
          "30 days（拒绝）至 5 years（允许）",
          "标准；已删除的对话会被移除",
          "自定义期限，最短 30 days；记入审计日志",
        ],
      ],
    },
    {
      group: "治理与合规",
      rows: [
        ["集中式管理控制", "—", "Seats、SSO、支出上限、使用分析", "+ SCIM、基于角色的访问、audit logs"],
        ["Compliance API（内容访问）", "—", "—", "有"],
        ["合规认证", "消费者方案——不涵盖", "SOC 2、ISO 27001 与 42001", "SOC 2、ISO 27001 与 42001"],
        ["提供 HIPAA BAA", "—", "—", "有——需销售协助"],
      ],
    },
  ],
  matrixFootnote:
    "整理自 Anthropic 的 Privacy Center、Trust Center 与 Help Center，截至 July 2026 准确。认证涵盖商业产品（Claude for Work 与 API），不涵盖消费者方案。",
  planEyebrow: "02 · 逐一方案看谁能看到您的对话",
  planHeading: "每个方案的坦诚答案",
  planPrivacy: [
    {
      tag: "Individual · Free / Pro / Max",
      title: "仅您私有——但训练由您选择",
      body: "没有管理员，没有组织。只有您能看到自己的对话，而 Anthropic 员工的访问受到限制（安全分类器可能会审查被标记的内容）。自 8 October 2025 起，由您决定是否让您的对话改进 Claude：允许则保留期长达 5 years，拒绝则保持在 30 days。",
    },
    {
      tag: "Team · 2–150 seats",
      title: "不会被随意监控——但 owner 可以导出",
      body: "普通管理员获得的是座位、支出与使用控制，而非窥探您对话的窗口。然而，Primary Owner 可以请求数据导出，其中可能包含您的对话与文件。您的数据绝不会用于训练。除非您共享 Project，否则个人对话保持私密。",
    },
    {
      tag: "Enterprise · 受监管与大型组织",
      title: "全面治理——包括内容访问",
      body: "包含 Team 的一切，另加 SSO/SCIM、基于角色的访问、audit logs、自定义保留，以及一个 Compliance API，让组织可以通过程序访问使用数据与对话内容，用于 eDiscovery 与监控。这是为那些必须能够访问并治理内容的组织而设计的方案——这是有意为之。",
    },
  ],
  trainingEyebrow: "03 · 训练与数据保留",
  trainingHeading: "Anthropic 会用您的数据训练模型吗？会保留多久？",
  trainingParaOneBefore: "最重要的单一区别在于",
  trainingParaOneEmphasis: "消费者与商业之别",
  trainingParaOneAfter:
    "。在 Team、Enterprise 与 API 上，Anthropic 默认不会使用您的提示与回复来训练其模型——这正是大多数企业应将敏感工作放在商业方案而非个人方案上的原因。",
  trainingParaTwo:
    "在个人 Free、Pro 与 Max 方案上，情况在 8 October 2025 发生变化：现在由您选择是否让对话改进 Claude。允许则保留期延长至五年；拒绝则适用标准的 30-day 窗口。删除对话会将其从您的历史记录中移除，之后会依据 Anthropic 的政策从其后端系统中清除。在 Enterprise 上，Primary Owner 或 Owner 设定自定义保留期——最短 30 days，若未配置期限则数据无限期保留——且每一次保留变更与删除都会写入 audit log。",
  pdpaEyebrow: "04 · 这对马来西亚的 PDPA 意味着什么",
  pdpaHeading: "在受 PDPA 监管的数据上使用 Claude",
  pdpaParaOne:
    "对于受《个人数据保护法》约束的马来西亚企业而言，模型只是一半。商业 Claude 方案为您提供了基本要素——不训练您的数据、可配置的保留期、SOC 2 与 ISO 27001/42001 保证，以及在 Enterprise 上受监管工作所需的 audit logs、基于角色的访问，和一份数据处理协议或 BAA。",
  pdpaParaTwoBefore: "但 PDPA 合规取决于",
  pdpaParaTwoEmphasis: "您如何部署与治理它",
  pdpaParaTwoAfter:
    "：尽量减少发送的个人数据、控制谁能访问、签署合适的处理者协议，并慎重考虑推理在何处运行。这正是我们与客户共同界定范围的工作——而且这是您的数据保护专员或法律顾问需要讨论的事，不是模型自行解决的。本页只是起点，而非法律意见。",
  faqEyebrow: "05 · 常见问题",
  faqs: [
    {
      question: "在 Team 或 Enterprise 方案上，我的公司或管理员能读取我的 Claude 对话吗？",
      answer:
        "无法通过普通的管理员仪表板做到。在 Team 方案上，普通管理员没有浏览您对话的功能——但组织的 Primary Owner 可以通过数据导出请求访问您的用户数据，其中可能包含您的对话、上传的文件与使用模式。在 Enterprise 上，组织还可以通过 Compliance API 以程序方式访问对话内容，并设定数据保留规则。因此在企业方案上，请把 Claude 当作工作电子邮件：它不会被随意监控，但当公司有正当理由时可以访问它。在个人 Free、Pro 或 Max 方案上没有管理员——只有您能看到自己的对话。",
    },
    {
      question: "Anthropic 会用我的对话训练 Claude 吗？",
      answer:
        "在 Team、Enterprise 与 API（包括 Amazon Bedrock 与 Google Vertex AI）上，不会——客户的提示与回复默认不会用于训练 Anthropic 的模型。在个人 Free、Pro 与 Max 方案上，规则在 8 October 2025 发生变化：现在由您选择。允许您的对话改进 Claude，保留期延长至 5 years；拒绝则适用标准的 30-day 保留期。",
    },
    {
      question: "我的 Claude 对话会保留多久？",
      answer:
        "在个人方案上，若您拒绝模型训练则为 30 days，若您允许则最长可达 5 years；删除对话会将其从您的历史记录中移除，随后会依据 Anthropic 的保留政策从其后端系统中移除。在 Team 上，适用标准保留期，已删除的对话会被移除。在 Enterprise 上，Primary Owner 或 Owner 设定自定义保留期（最短 30 days；若未设定期限则数据无限期保留），且所有保留变更与删除都会记入 audit logs。",
    },
    {
      question: "Claude 符合 SOC 2 或 HIPAA 合规吗？",
      answer:
        "对于其商业产品——Claude for Work（Team 与 Enterprise）与 Anthropic API——Anthropic 维持 SOC 2 Type I 与 Type II、ISO/IEC 27001:2022 与 ISO/IEC 42001:2023，并提供一套符合 HIPAA 要求的配置，在需销售协助的 Enterprise 与第一方 API 上可签署 Business Associate Agreement（BAA）。消费者 Free/Pro/Max 方案并非承担这些承诺的载体。当前认证与子处理者清单发布在 Anthropic 的 Trust Center。",
    },
    {
      question: "我可以在马来西亚将 Claude 用于受 PDPA 监管的个人数据吗？",
      answer:
        "可以，前提是选对方案并正确设置。商业方案不会训练您的数据，并支持保留控制；Enterprise 还增加了 audit logs、基于角色的访问、Compliance API，以及一条 BAA/数据处理协议的路径。但 PDPA 合规主要取决于您如何部署与治理 Claude——数据最小化、访问控制、处理者协议，以及推理在何处运行——而不仅仅是模型本身。请把本页当作您的 DPO 或法律顾问的起点，而非法律意见。",
    },
    {
      question: "我的对话对团队中的其他人是私密的吗？",
      answer:
        "是的。除非您明确共享某个 Project 或对话，否则您的个人对话仅您私有。共享 Project 会让您选择的人获得对该项目对话、知识与指令的 'Can view' 或 'Can edit' 访问权限——但您的其他个人对话保持私密，不会暴露给队友。",
    },
    {
      question: "在隐私与合规方面，Team 与 Enterprise 有何区别？",
      answer:
        "两者都会让您的数据不进入模型训练，并为您提供集中式管理与 SSO。Enterprise 还增加了 SCIM 配置、细粒度的基于角色的访问控制、audit logs、用于以程序方式访问使用数据与内容的 Compliance API、自定义数据保留时间线，以及诸如客户管理的加密密钥、仅限 US 的推理，和 HIPAA BAA 等选项——这些正是受监管组织通常所需的治理控制。",
    },
  ],
  sourcesEyebrow: "06 · 来源",
  sourcesHeading: "对照 Anthropic 自己的文档核实",
  sourcesIntro:
    "本页的每一项主张均取自 Anthropic 的第一手来源，在此列出，以便您可以直接核实每一点，而非仅凭我们的说法：",
  sources: [
    {
      href: "https://privacy.claude.com/en/articles/9265372-who-owns-and-manages-the-data-of-my-team",
      label: "Anthropic Privacy Center — Who owns and manages the data of my team?",
      note: "——Primary Owner 可以通过数据导出请求访问成员数据，其中可能包含对话、文件与使用情况。",
    },
    {
      href: "https://www.anthropic.com/news/claude-code-on-team-and-enterprise",
      label: "Anthropic — Claude Code and new admin controls for business plans",
      note: "——Enterprise Compliance API 提供对使用数据与客户内容的实时程序化访问。",
    },
    {
      href: "https://www.anthropic.com/news/updates-to-our-consumer-terms",
      label: "Anthropic — Updates to Consumer Terms and Privacy Policy",
      note: "——针对 Free/Pro/Max 的 8 October 2025 模型训练选择，以及 5-year 与 30-day 的保留差异；不适用于商业方案。",
    },
    {
      href: "https://privacy.claude.com/en/articles/10440198-custom-data-retention-controls-for-claude-enterprise",
      label: "Anthropic Privacy Center — Custom data retention controls for Claude Enterprise",
      note: "——最短 30-day 保留、默认无限期，以及对所有保留变更的审计记录。",
    },
    {
      href: "https://privacy.claude.com/en/articles/10015870-do-you-have-a-soc-2-or-hipaa-certifications",
      label: "Anthropic Privacy Center — Do you have SOC 2 or HIPAA certifications?",
      note: "——SOC 2 Type I & II、ISO/IEC 27001:2022、ISO/IEC 42001:2023，以及为商业产品提供 BAA 的 HIPAA 就绪配置。",
    },
    {
      href: "https://trust.anthropic.com",
      label: "Anthropic Trust Center",
      note: "——权威且始终最新的认证与子处理者清单。",
    },
  ],
  disclaimerLabel: "免责声明",
  disclaimerBeforeLink:
    "本页的所有隐私与合规信息均整理自 Anthropic 的 Privacy Center、Trust Center 与 Help Center 上公开可得的信息，仅供参考与研究之用，截至 July 2026 准确，并可能由 Anthropic 自行决定变更。它不是法律意见；请与您自己的数据保护专员或法律顾问确认您的义务。Anchor Sprint 是部署与推行合作伙伴，而非经销商。Claude and Anthropic are trademarks of Anthropic, PBC. Anchor Sprint is Certified in the Claude Partner Network；这并不意味着我们的服务获得背书。有关权威的安全与合规文档，请参阅 Anthropic 的 ",
  disclaimerLinkText: "Trust Center",
  disclaimerAfterLink: "。",
  keepReadingLabel: "继续阅读",
  readingLinks: [
    { href: "/guides/claude-cowork/", label: "什么是 Claude Cowork？" },
    { href: "/guides/claude-plans-malaysia/", label: "比较每一个 Claude 方案" },
    { href: "/blog/claude-ai-pricing-malaysia-2026/", label: "马来西亚的 Claude AI 价格" },
    { href: "/services/ai-solutions/", label: "探索我们的 AI Solutions" },
  ],
  ctaTitle: "要在敏感或受 PDPA 监管的数据上部署 Claude 吗？",
  ctaDescription:
    "告诉我们您的团队规模、Claude 将接触的数据，以及您的合规义务。我们将推荐合适的方案，并配置一套符合 PDPA 的部署——访问控制、保留与处理者协议——然后协助您推行。我们不转售 seats。",
  ctaButtonText: "联系我们的团队",
};

export function getClaudePrivacyComplianceContent(locale: Locale): Content {
  return locale === "ms" ? ms : locale === "zh" ? zh : en;
}
